import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  ImagePlus,
  Lock,
  ArrowUpRight,
  BookOpen,
  FileText,
  Image as ImageIcon,
  Download,
  AlertTriangle,
  CheckCircle2,
  PencilLine,
  Layers,
} from "lucide-react";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * Plain-English, example-driven reference for the Bulk Product Photo Upload
 * feature (Pro only) — what it does, requirements, what happens to your
 * catalog right after upload, and how to finish the resulting drafts.
 * Companion to CatalogGuide.jsx, same design system.
 */

const ACCENTS = {
  lilac: { chip: "bg-lilac", chipText: "text-ink", soft: "bg-lilac/25", ring: "ring-lilac" },
  mint: { chip: "bg-mint", chipText: "text-ink", soft: "bg-mint/25", ring: "ring-mint" },
  peach: { chip: "bg-peach", chipText: "text-ink", soft: "bg-peach/25", ring: "ring-peach" },
  hotpink: { chip: "bg-hotpink", chipText: "text-white", soft: "bg-hotpink/10", ring: "ring-hotpink" },
  ink: { chip: "bg-ink", chipText: "text-cream", soft: "bg-ink/[0.06]", ring: "ring-ink" },
};

function FieldCard({ icon: Icon, label, desc, example, note, accent }) {
  const a = ACCENTS[accent];
  return (
    <div className="rounded-2xl border border-ink/10 bg-white p-4">
      <div className="flex items-center gap-2">
        <span className={`grid h-7 w-7 flex-none place-items-center rounded-lg ${a.chip} ${a.chipText}`}>
          <Icon size={13} strokeWidth={2.4} />
        </span>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-ink">{label}</p>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-ink/70">{desc}</p>
      {example && (
        <div className={`mt-2.5 rounded-xl ${a.soft} px-3 py-2`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-ink/50">Example</p>
          <p className="mt-0.5 text-[12px] leading-relaxed text-ink/80">{example}</p>
        </div>
      )}
      {note && <p className="mt-2 text-[11px] leading-relaxed text-ink/45">{note}</p>}
    </div>
  );
}

function GuideSection({ id, icon: Icon, accent, name, tagline, what, useFor, checkWhen, fields, planNote, children }) {
  const a = ACCENTS[accent];
  return (
    <section id={id} className="scroll-mt-24 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <span className={`grid h-12 w-12 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText}`}>
            <Icon size={20} strokeWidth={2.2} />
          </span>
          <div>
            <p className={`text-xs font-bold uppercase tracking-[0.2em] ${accent === "hotpink" ? "text-hotpink" : "text-ink/50"}`}>
              {tagline}
            </p>
            <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">{name}</h2>
          </div>
        </div>
        {planNote && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-ink/[0.03] px-3 py-1.5 text-[11px] font-semibold text-ink/60">
            <Lock size={11} /> {planNote}
          </span>
        )}
      </div>

      {what && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">{what}</p>}

      {(useFor || checkWhen) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {useFor && (
            <div className="rounded-2xl bg-ink/[0.03] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">Use it for</p>
              <p className="mt-1 text-[13px] leading-relaxed text-ink/70">{useFor}</p>
            </div>
          )}
          {checkWhen && (
            <div className="rounded-2xl bg-ink/[0.03] p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">Check it when</p>
              <p className="mt-1 text-[13px] leading-relaxed text-ink/70">{checkWhen}</p>
            </div>
          )}
        </div>
      )}

      {children}

      {fields && fields.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-ink/45">What you'll see, field by field</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {fields.map((f) => (
              <FieldCard key={f.label} accent={accent} {...f} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default function BulkImageUploadGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Bulk Product Photo Upload Guide",
    description: "How to upload up to 50 product photos at once on the Pro plan and turn them into draft products ready to finish and publish.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/bulk-image-upload-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Bulk Product Photo Upload Guide", item: "https://checkoutdm.com/bulk-image-upload-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Bulk Product Photo Upload Guide | Checkout DM"
        description="How Pro merchants upload up to 50 product photos at once and turn them straight into draft products ready to finish."
        path="/bulk-image-upload-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          Uploading product photos in bulk
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          A Pro-only shortcut for stocking a large catalog fast — select up to 50 product
          photos in one go and each one becomes its own draft product in your catalog
          immediately, ready for you to finish with a title and price.
        </p>
        <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          <Link to="/catalog-guide" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <BookOpen size={13} /> What the Catalog screen means →
          </Link>
        </p>

        {/* Quick nav */}
        <div className="mt-6 flex flex-wrap gap-2">
          {[
            { id: "overview", label: "Overview", icon: ImagePlus, accent: "lilac" },
            { id: "how-to", label: "How to upload", icon: Download, accent: "mint" },
            { id: "requirements", label: "Requirements", icon: FileText, accent: "peach" },
            { id: "after-upload", label: "After upload", icon: Layers, accent: "lilac" },
            { id: "finishing", label: "Finishing your drafts", icon: PencilLine, accent: "mint" },
            { id: "troubleshooting", label: "Troubleshooting", icon: AlertTriangle, accent: "hotpink" },
          ].map((t) => {
            const a = ACCENTS[t.accent];
            return (
              <a
                key={t.id}
                href={`#${t.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full ${a.chip} ${a.chipText} px-3.5 py-1.5 text-xs font-bold`}
              >
                <t.icon size={12} /> {t.label}
              </a>
            );
          })}
        </div>

        <div className="mt-8 space-y-6">
          {/* Overview */}
          <GuideSection
            id="overview"
            icon={ImagePlus}
            accent="lilac"
            name="Bulk photo upload"
            tagline="Stock a large catalog fast"
            planNote="Pro only"
            what="Instead of adding products one at a time through the Catalog screen, Pro merchants can select a batch of JPG photos in a single step from the 'Bulk upload photos' button on the Catalog page. Each photo you upload becomes its own draft product in your catalog right away — there's no separate photo-hosting step and nothing extra to paste anywhere."
            useFor="Getting a large batch of products started in one go — especially useful when you're listing dozens of items at once and don't want to open the Add product form separately for every photo."
            checkWhen="You're about to list a large batch of new products and have all their photos ready on your device."
          />

          {/* How to upload */}
          <GuideSection
            id="how-to"
            icon={Download}
            accent="mint"
            name="How to upload"
            tagline="Three steps"
            fields={[
              {
                icon: ImagePlus,
                label: "1. Open the Catalog page",
                desc: "Click 'Bulk upload photos' next to 'Export CSV' at the top of your Catalog screen. Both buttons are visible to Pro merchants only.",
              },
              {
                icon: FileText,
                label: "2. Select your photos",
                desc: "Pick up to 50 JPG files at once from your device's file picker. There's no requirement to select exactly 50 — any number from 1 to 50 works.",
                note: "If you select more than 50, only the first 50 are uploaded — the rest are ignored, so it's worth uploading in separate batches instead.",
              },
              {
                icon: CheckCircle2,
                label: "3. Wait for the upload to finish",
                desc: "While your photos upload, the button shows 'Uploading…'. Once it's done, a new draft product card appears at the top of your catalog grid for every photo you selected.",
              },
            ]}
          />

          {/* Requirements */}
          <GuideSection
            id="requirements"
            icon={FileText}
            accent="peach"
            name="Requirements"
            tagline="Same rules as single-photo upload"
            fields={[
              {
                icon: ImageIcon,
                label: "File type",
                desc: "JPG only. Other formats (PNG, WEBP, HEIC) are rejected — the same rule as adding a single product photo.",
                note: "If your phone saves photos as HEIC, convert or export as JPG before uploading.",
              },
              {
                icon: FileText,
                label: "File size",
                desc: "Up to 1 MB per photo — the same per-image limit as the single-product upload flow.",
              },
              {
                icon: ImagePlus,
                label: "Batch size",
                desc: "Up to 50 photos per upload. Need to do more than 50? Just repeat the process in another batch once the first one finishes.",
              },
              {
                icon: Lock,
                label: "Plan requirement",
                desc: "This feature only appears for Pro merchants. On Free, Starter, or Growth, product photos are added one at a time through the regular Catalog edit form.",
              },
            ]}
          />

          {/* After upload */}
          <GuideSection
            id="after-upload"
            icon={Layers}
            accent="lilac"
            name="What happens right after upload"
            tagline="Draft products, not just hosted photos"
            what="Every photo you selected turns into its own draft product, added to the top of your catalog grid in the order you picked the files. Nothing is left half-finished behind the scenes — each draft is already a real product row, just an incomplete one."
            fields={[
              {
                icon: ImageIcon,
                label: "Placeholder title",
                desc: "Every draft is labeled 'Untitled product — tap Edit to finish' until you rename it.",
              },
              {
                icon: FileText,
                label: "Zero price",
                desc: "Price starts at 0 for every draft — the bot won't quote a real price to a customer until you set one.",
              },
              {
                icon: Lock,
                label: "Hidden from customers",
                desc: "Drafts are marked sold out / hidden from DMs from the moment they're created, so nothing half-finished ever reaches a customer in a chat.",
              },
            ]}
          />

          {/* Finishing your drafts */}
          <GuideSection
            id="finishing"
            icon={PencilLine}
            accent="mint"
            name="Finishing your drafts"
            tagline="One quick edit per photo"
            what="A bulk upload gets your photos into the catalog fast, but each product still needs a title and a price before the bot will offer it to customers — the same as any product you add one at a time."
            fields={[
              {
                icon: PencilLine,
                label: "Edit each draft",
                desc: "Tap Edit on a draft card just like any other product. Fill in the title and price at minimum — quantity, sizes, and description are optional, same as always.",
              },
              {
                icon: CheckCircle2,
                label: "Live the moment you save",
                desc: "Once you hit Save on a draft, it behaves exactly like any other product — it comes off the hidden/sold-out state and the bot can offer it to customers right away.",
              },
              {
                icon: Download,
                label: "Export CSV is separate",
                desc: "Export CSV is a good way to review everything at once — including which drafts still need finishing — but it isn't a required step for bulk upload itself.",
              },
            ]}
          />

          {/* Troubleshooting */}
          <GuideSection
            id="troubleshooting"
            icon={AlertTriangle}
            accent="hotpink"
            name="Troubleshooting"
            tagline="Common upload errors"
            fields={[
              {
                icon: AlertTriangle,
                label: "\"Unsupported image type\"",
                desc: "One or more selected files aren't JPGs. Convert them to JPG and try again.",
              },
              {
                icon: AlertTriangle,
                label: "\"Image too large\"",
                desc: "One or more photos are over 1 MB. Compress or resize the photo and re-upload.",
              },
              {
                icon: ImagePlus,
                label: "I selected more than 50 and some are missing",
                desc: "Only the first 50 files you selected are uploaded — anything past that is silently skipped. Select and upload the rest as a second batch.",
              },
              {
                icon: Lock,
                label: "The button isn't visible at all",
                desc: "Bulk photo upload is only shown to Pro merchants. If you're on Free, Starter, or Growth, upgrade to Pro to unlock it, or add photos individually through the regular Catalog edit form.",
              },
            ]}
          />
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Still stuck?</p>
            <p className="mt-1 max-w-md text-sm text-ink/70">
              If something about bulk photo upload still doesn't make sense after reading this, reach out and we'll add it here for the next person too.
            </p>
          </div>
          <a
            href="/plans"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
          >
            See plans <ArrowUpRight size={14} />
          </a>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}